/**
 * ChromaTunePanel — Admin-UI für Chroma-Key-Feintuning eines Branding-Videos.
 *
 * Sichtbar, sobald in `site_content['brand_logo']` ein Video-Media-Doc ausgewählt
 * ist. Erlaubt live:
 *   - Toggle Chroma-Key ein/aus
 *   - Color-Picker für die Chroma-Farbe (default: aus Media-Doc)
 *   - Slider `similarity` (0.05 – 0.5)
 *   - Slider `blend`      (0.0  – 0.2)
 *   - Slider `despill`    (0.0  – 1.0)
 *   - Live-Preview: `ChromaKeyVideo` rendert mit den aktuellen Slider-Werten
 *   - Speichern → PATCH /api/admin/media/{id} mit `chroma_key` → invalidiert
 *     den site-content-Cache, damit der Header live refetcht.
 *   - Reset → auf empfohlene Defaults (die aus dem Tuning-Skript).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Save, RotateCcw, Wand2, AlertCircle } from "lucide-react";
import { api } from "@/lib/auth";
import { invalidateSiteContentCache } from "@/lib/siteContent";
import ChromaKeyVideo, { type ChromaKeyConfig } from "@/components/ChromaKeyVideo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

// Konservative, empirisch getunte Defaults (aus tune_chroma_key.py).
const DEFAULT_CHROMA: ChromaKeyConfig = {
    color: "#7A9B1F",
    similarity: 0.28,
    blend: 0.03,
    despill: 0.0,
};

type MediaInfo = {
    id: string;
    media_type?: string;
    chroma_key?: ChromaKeyConfig | null;
    webm_url?: string | null;
    title?: string;
};

interface Props {
    /** media_id aus BrandLogoPanel (dieselbe Auswahl, damit tuning und Live-Auswahl konsistent bleiben). */
    mediaId: string | null;
}

export default function ChromaTunePanel({ mediaId }: Props) {
    const [info, setInfo] = useState<MediaInfo | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [config, setConfig] = useState<ChromaKeyConfig>(DEFAULT_CHROMA);
    const [savedConfig, setSavedConfig] = useState<ChromaKeyConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // Media-Info laden.
    useEffect(() => {
        setErr(null);
        setInfo(null);
        setSavedConfig(null);
        if (!mediaId) return;
        setLoading(true);
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/media/${mediaId}/info`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = (await res.json()) as MediaInfo;
                if (cancelled) return;
                setInfo(data);
                if (data.chroma_key && data.chroma_key.color) {
                    setEnabled(true);
                    setConfig({
                        color: data.chroma_key.color,
                        similarity: data.chroma_key.similarity ?? DEFAULT_CHROMA.similarity,
                        blend: data.chroma_key.blend ?? DEFAULT_CHROMA.blend,
                        despill: data.chroma_key.despill ?? DEFAULT_CHROMA.despill,
                    });
                    setSavedConfig(data.chroma_key);
                } else {
                    setEnabled(false);
                    setConfig(DEFAULT_CHROMA);
                    setSavedConfig(null);
                }
            } catch (e) {
                if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [mediaId]);

    const isVideo = info?.media_type === "video" || info?.media_type === "video_360";

    const hasChanges = useMemo(() => {
        if (!enabled && savedConfig === null) return false;
        if (enabled && savedConfig === null) return true;
        if (!enabled && savedConfig !== null) return true;
        if (!savedConfig) return false;
        return savedConfig.color !== config.color
            || savedConfig.similarity !== config.similarity
            || savedConfig.blend !== config.blend
            || savedConfig.despill !== config.despill;
    }, [enabled, config, savedConfig]);

    const doSave = useCallback(async () => {
        if (!mediaId) return;
        setSaving(true);
        setErr(null);
        try {
            const payload = enabled ? { chroma_key: config } : { chroma_key: null };
            const res = await api(`/api/admin/media/${mediaId}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(res.error || `HTTP ${res.status}`);
            setSavedConfig(enabled ? config : null);
            invalidateSiteContentCache();
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
        } finally {
            setSaving(false);
        }
    }, [mediaId, enabled, config]);

    if (!mediaId) return null;
    if (loading) {
        return (
            <section className="p-5 md:p-6 rounded-md mb-6"
                     style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))" }}>
                <div className="text-sm" style={{ color: "hsl(var(--hf-muted))" }}>Lade Chroma-Key-Einstellungen…</div>
            </section>
        );
    }
    if (!isVideo) return null; // Chroma-Key nur für Videos

    const proxySrc = `${BACKEND_URL}/api/media/${mediaId}/proxy`;
    const webmSrc = info?.webm_url ? `${BACKEND_URL}${info.webm_url}` : null;

    return (
        <section
            className="p-5 md:p-6 rounded-md mb-6"
            style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))", boxShadow: "var(--shadow-warm-sm)" }}
            data-testid="admin-chroma-tune-panel"
        >
            <div className="flex items-baseline justify-between gap-4 mb-4 flex-wrap">
                <div className="min-w-0">
                    <div className="hf-eyebrow" style={{ fontSize: 10 }}>Branding · Video-Transparenz</div>
                    <h2 className="font-display text-xl md:text-2xl leading-tight mt-0.5">Chroma-Key Feintuning</h2>
                    <p className="text-[13px] mt-1" style={{ color: "hsl(var(--hf-muted))" }}>
                        Nur nötig wenn das Logo-Video einen einfarbigen (meist grünen) Hintergrund hat, der ausgeschnitten werden soll. Ändere die Slider und beobachte die Vorschau — dann speichern.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={() => setConfig(DEFAULT_CHROMA)}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-sm border"
                        style={{ borderColor: "hsl(var(--hf-line))", color: "hsl(var(--hf-muted))" }}
                        data-testid="admin-chroma-reset"
                    >
                        <RotateCcw size={12} /> Defaults
                    </button>
                    <button
                        type="button"
                        onClick={doSave}
                        disabled={!hasChanges || saving}
                        data-testid="admin-chroma-save"
                        className="inline-flex items-center gap-2 text-xs md:text-sm px-4 py-2 rounded-sm font-medium disabled:opacity-40"
                        style={{ background: hasChanges ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))", color: "hsl(var(--hf-ink))" }}
                    >
                        <Save size={14} /> {saving ? "Speichere…" : hasChanges ? "Speichern" : "Gespeichert"}
                    </button>
                </div>
            </div>

            {err && (
                <div className="mb-4 p-3 rounded-sm text-sm flex items-start gap-2"
                     style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}
                     data-testid="admin-chroma-error">
                    <AlertCircle size={16} className="mt-0.5" /> {err}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
                {/* Kontrollen */}
                <div className="space-y-4">
                    <label className="flex items-center gap-3 text-sm">
                        <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setEnabled(e.target.checked)}
                            className="h-4 w-4"
                            data-testid="admin-chroma-enable"
                        />
                        <span className="flex items-center gap-1.5">
                            <Wand2 size={14} /> Chroma-Key aktiv
                        </span>
                    </label>

                    <div className={enabled ? "" : "opacity-40 pointer-events-none select-none"}>
                        <div className="flex items-center gap-3 mb-3">
                            <label className="text-[12px] uppercase tracking-wider" style={{ color: "hsl(var(--hf-muted))" }}>
                                Chroma-Farbe
                            </label>
                            <input
                                type="color"
                                value={config.color}
                                onChange={(e) => setConfig((c) => ({ ...c, color: e.target.value }))}
                                className="h-8 w-16 border rounded-sm cursor-pointer"
                                style={{ borderColor: "hsl(var(--hf-line))" }}
                                data-testid="admin-chroma-color"
                            />
                            <span className="font-mono text-xs" style={{ color: "hsl(var(--hf-muted))" }}>{config.color.toUpperCase()}</span>
                        </div>

                        <SliderRow
                            label="Similarity"
                            hint="Größer → mehr Farb-Nähe wird ausgeschnitten (grüner Halo entfernt, aber Motiv-Grün-Anteile ebenfalls)"
                            min={0.05} max={0.5} step={0.01}
                            value={config.similarity ?? DEFAULT_CHROMA.similarity!}
                            onChange={(v) => setConfig((c) => ({ ...c, similarity: v }))}
                            testId="admin-chroma-similarity"
                        />
                        <SliderRow
                            label="Blend"
                            hint="Weichheit des Übergangs zwischen transparent und opak"
                            min={0} max={0.2} step={0.005}
                            value={config.blend ?? DEFAULT_CHROMA.blend!}
                            onChange={(v) => setConfig((c) => ({ ...c, blend: v }))}
                            testId="admin-chroma-blend"
                        />
                        <SliderRow
                            label="Despill"
                            hint="Grün-Entsättigung an den Kanten (0 = aus; höher entzieht dem Motiv Gold-/Grüntöne)"
                            min={0} max={1} step={0.05}
                            value={config.despill ?? DEFAULT_CHROMA.despill!}
                            onChange={(v) => setConfig((c) => ({ ...c, despill: v }))}
                            testId="admin-chroma-despill"
                        />
                    </div>
                </div>

                {/* Live-Preview */}
                <div>
                    <div className="hf-eyebrow mb-1" style={{ fontSize: 10 }}>Live-Vorschau (mit rotem Kontrasthintergrund)</div>
                    <div
                        className="rounded-sm overflow-hidden flex items-center justify-center relative"
                        style={{
                            aspectRatio: "16 / 9",
                            background: "repeating-linear-gradient(45deg, #c22, #c22 12px, #a11 12px, #a11 24px)",
                            border: "1px solid hsl(var(--hf-line))",
                        }}
                        data-testid="admin-chroma-preview"
                    >
                        {enabled ? (
                            <ChromaKeyVideo
                                key={`preview-${mediaId}-${config.color}`}
                                src={proxySrc}
                                srcWebm={webmSrc}
                                chroma={config}
                                className="absolute inset-0 w-full h-full object-contain"
                                testId="admin-chroma-preview-canvas"
                                ariaLabel="Chroma-Key-Vorschau"
                            />
                        ) : (
                            <video
                                key={`plain-${mediaId}`}
                                src={proxySrc}
                                autoPlay muted loop playsInline
                                className="absolute inset-0 w-full h-full object-contain"
                                data-testid="admin-chroma-preview-plain"
                            />
                        )}
                    </div>
                    <p className="text-[11px] mt-2" style={{ color: "hsl(var(--hf-muted))" }}>
                        Rotes Karo-Muster = simulierter Kontrast. In der Realität liegt das Video auf dem Header-Hintergrund.
                    </p>
                </div>
            </div>
        </section>
    );
}

function SliderRow({ label, hint, min, max, step, value, onChange, testId }: {
    label: string;
    hint: string;
    min: number; max: number; step: number;
    value: number;
    onChange: (v: number) => void;
    testId: string;
}) {
    return (
        <div className="mb-3">
            <div className="flex items-baseline justify-between gap-3">
                <label className="text-[12px] uppercase tracking-wider" style={{ color: "hsl(var(--hf-muted))" }}>
                    {label}
                </label>
                <span className="font-mono text-xs" style={{ color: "hsl(var(--hf-ink))" }}>
                    {value.toFixed(3)}
                </span>
            </div>
            <input
                type="range"
                min={min} max={max} step={step} value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
                className="w-full mt-1"
                data-testid={testId}
            />
            <p className="text-[11px] mt-0.5" style={{ color: "hsl(var(--hf-muted))" }}>{hint}</p>
        </div>
    );
}
